// Who owns a file's checklist item (Henry 10/08/2026: "We need to fix the coordinators and what they
// do because the tasks aren't correlating with the people"). THE rule, in one place, for both ends:
//   - seeding: taskSections.expandStageItems asks seedOwner for every row it puts on a deal (turn-ins,
//     board drags, the stage pill, Edit Deal, lead conversions, file requests, the Repair Tools backfill);
//   - re-pointing: Settings > Coordinators "Also Move Their Open Tasks" (one agent, after a dropdown
//     change) and "Fix Task Owners" (every live file) use ownerFixesForDeal, so an item already on a
//     file is pointed at exactly the person a brand new copy of it would get today.
// The people come from Settings > Coordinators through listingPrep.coordinatorsFor (the agent's
// Listing Coordinator, their Transaction / Client Care person, the team's Backup Client Care Person).
// Pure: no Firebase, no React. Imports nothing from taskSections (it imports this file).
import { coordinatorsFor, cleanDealTask, isOpenTask, toYMD, todayYMD } from "./listingPrep";
import { inScopeDeal, activeOwnerOf } from "./checklistScope";

// Stages where Client Care owns a listing's items (My Checklist spec 09/27/2026, decision 4).
export const CONTRACT_STAGES = ["Under Contract", "Pending", "Closed", "Cancelled", "Expired"];

const norm = (v) => String(v ?? "").trim();
const isBuyer = (dealtype) => norm(dealtype).toLowerCase() === "buyer";
const fullName = (p) => `${(p && p.firstname) || ""} ${(p && p.lastname) || ""}`.trim();
const namedPerson = (p) => (p && p.id ? { id: String(p.id), firstname: p.firstname || "", lastname: p.lastname || "" } : null);

// The kind of job a row is:
//   "lc"    the agent's Listing Coordinator
//   "tc"    the agent's Transaction Coordinator (TC-flagged rows before a listing goes under contract)
//   "cc"    the agent's Client Care person (else the team's backup)
//   "named" a person Henry named on the template row keeps it
// Buyer file: every row is Client Care's unless a person is named on it (an LC flag on a buyer row is
// a listing job by mistake). Listing at Under Contract or later: LC-flagged rows stay with the LC, the
// rest go to Client Care. Listing before that: TC-flagged rows to the TC, everything else to the LC.
// `named` overrides the row's own "is a person named on it" (an item already on a deal always carries
// an assignee, so re-pointing asks the template instead).
export const ownerKeyFor = (row, stage, dealtype, { named } = {}) => {
  const lc = !!(row && row.coordinatorRole);
  const tc = !!(row && row.transactionCoordinatorRole);
  const isNamed = !lc && !tc && (named === undefined ? !!(row && row.assignee && row.assignee.id) : !!named);
  if (isBuyer(dealtype)) return isNamed ? "named" : "cc";
  if (CONTRACT_STAGES.includes(norm(stage))) {
    if (lc) return tc ? "tc" : "lc";
    return isNamed ? "named" : "cc";
  }
  if (tc) return "tc";
  if (lc) return "lc";
  return isNamed ? "named" : "lc";
};

// The person for a job kind, from coordinatorsFor's { coordinator, transactionCoordinator, clientCare }.
// Callers that pass no clientCare fall back to the transactionCoordinator (older seeders).
export const ownerForKey = (key, owners = {}) => {
  const o = owners || {};
  if (key === "lc") return o.coordinator || null;
  if (key === "tc") return o.transactionCoordinator || null;
  if (key === "cc") return (o.clientCare === undefined ? o.transactionCoordinator : o.clientCare) || null;
  return null;
};

// What a NEW item from a template row gets: its owner and whether the catch-all rule sent it to the
// LC (routedToLC; an LC-flagged row is the LC's by its flag, not by the catch-all).
export const seedOwner = (row, stage, dealtype, owners) => {
  const key = ownerKeyFor(row, stage, dealtype);
  if (key === "named") return { assignee: (row && row.assignee) || null, routedToLC: false };
  const assignee = ownerForKey(key, owners);
  return { assignee: assignee || null, routedToLC: key === "lc" && !(row && row.coordinatorRole) && !!assignee };
};

// The people one file's items go to: its agent's coordinators (Settings > Coordinators).
export const dealOwners = (deal, users, backupId = null) => coordinatorsFor(deal && deal.agentData, users, backupId);

// ---- Re-pointing items already on files ----

// Rows Henry named a person on in the Task Templates, as "stage|id" (template ids repeat across
// stages). templates: { listing, buyer } or one template object.
export const namedTemplateRows = (templates) => {
  const out = new Set();
  const add = (template) =>
    Object.entries(template || {}).forEach(([stage, items]) =>
      (Array.isArray(items) ? items : []).forEach((it) => {
        if (it && it.type !== "section" && !it.coordinatorRole && !it.transactionCoordinatorRole && it.assignee && it.assignee.id) {
          out.add(`${norm(stage)}|${norm(it.id)}`);
        }
      }),
    );
  const t = templates || {};
  if (t.listing || t.buyer) {
    add(t.listing);
    add(t.buyer);
  } else add(t);
  return out;
};

// A task a person typed in on the Task tab gets id Date.now() (13 digits); template copies carry small
// running numbers. Typed-in items are whoever they were given to, never the rule's.
export const addedByHand = (t) => {
  const n = Number(t && t.id);
  return Number.isFinite(n) && n >= 1e12;
};

// Given out by hand: someone picked the person on the Task tab, My Checklist (Assign, Take Them) or an
// older Move Them. An item this tool pointed (ownerFixedTo, still with that person) is the rule's.
export const handedOutByHand = (t) =>
  !!(t && norm(t.handedOffBy)) && !(t.ownerFixedTo && t.assignee && String(t.assignee.id) === String(t.ownerFixedTo));

// The job and the person an item on a deal should have now.
export const expectedOwnerOf = (task, deal, { owners, named } = {}) => {
  const stage = norm(task && task.stage);
  const key = ownerKeyFor(task, stage, deal && deal.dealtype, { named: !!(named && named.has(`${stage}|${norm(task && task.id)}`)) });
  return { key, owner: key === "named" ? null : ownerForKey(key, owners) };
};

// Every open item on one file whose owner does not match the rule, and what to do with it.
// ctx: { owners (dealOwners of THIS deal), users, named (namedTemplateRows), keys (only these job
// kinds, e.g. ["lc"]; null = all), includeHandOffs, alsoFrom (person ids whose items move even when
// given out by hand: the agent's previous coordinator) }.
// Left alone: Completed and N/A items, items with no stage (legacy, never on My Checklist), typed-in
// items, rows with a named person, items already with the right person, and (unless includeHandOffs)
// items given out by hand or held by the file's own agent. An item whose job has nobody to go to is
// never taken away from anyone: it is only counted (noOneToGoTo) when it has no owner either.
// Returns { moves: [{ index, task, key, to, from }], handOffs, noOneToGoTo, noStage }.
export const ownerFixesForDeal = (tasks, deal, ctx = {}) => {
  const { owners = {}, users = [], named = new Set(), keys = null, includeHandOffs = false, alsoFrom = [] } = ctx;
  const also = new Set((alsoFrom || []).filter(Boolean).map(String));
  const moves = [];
  let handOffs = 0;
  let noOneToGoTo = 0;
  let noStage = 0;
  (Array.isArray(tasks) ? tasks : []).forEach((t, index) => {
    if (!t || !isOpenTask(t) || addedByHand(t)) return;
    if (!norm(t.stage)) {
      noStage += 1;
      return;
    }
    const { key, owner: to } = expectedOwnerOf(t, deal, { owners, named });
    if (key === "named" || (keys && !keys.includes(key))) return;
    const current = activeOwnerOf(t, users);
    if (!to) {
      if (!current) noOneToGoTo += 1;
      return;
    }
    if (current && String(current.id) === String(to.id)) return;
    if (current && !also.has(String(current.id))) {
      const byHand = handedOutByHand(t) || String(current.id) === String(deal && deal.agentData);
      if (byHand && !includeHandOffs) {
        handOffs += 1;
        return;
      }
    }
    moves.push({ index, task: t, key, to, from: current || null });
  });
  return { moves, handOffs, noOneToGoTo, noStage };
};

const dealIdOf = (deal) => String((deal && (deal.createdat || deal.id)) || "");

// The preview for a run over many files. scope: { agentId } limits it to one agent's files.
// Files: live ones only (checklistScope.inScopeDeal: not deleted, archived, Cancelled, Expired, a
// parked file request, a test file, or Closed more than 30 days). A test file is nobody's work
// (utils/testFiles.js), so it is never given owners.
// Returns {
//   deals: [{ dealId, address, count }]        one entry per file that changes (one write each)
//   total, handOffs, noStage (open items with no stage: older files and Gmail add-on turn-ins, left alone),
//   byPerson: [{ id, name, count, from: [{ name, count }] }]   who gets items, and from whom
//   noOneToGoTo: [{ agentId, name, count }]   items with no owner whose agent has nobody set
// }
export const planOwnerFixes = ({ deals, users, backupId = null, named, today, agentId = null, keys = null, includeHandOffs = false, alsoFrom = [] } = {}) => {
  const day = toYMD(today) || todayYMD();
  const people = Array.isArray(users) ? users : [];
  const byPerson = new Map();
  const noOne = new Map();
  const out = { deals: [], total: 0, handOffs: 0, noStage: 0, byPerson: [], noOneToGoTo: [] };
  (Array.isArray(deals) ? deals : []).forEach((deal) => {
    if (!inScopeDeal(deal, day)) return;
    if (agentId && String(deal.agentData || "") !== String(agentId)) return;
    const owners = dealOwners(deal, people, backupId);
    const r = ownerFixesForDeal(deal.tasks, deal, { owners, users: people, named, keys, includeHandOffs, alsoFrom });
    out.handOffs += r.handOffs;
    out.noStage += r.noStage;
    if (r.noOneToGoTo) {
      const id = String(deal.agentData || "");
      const agent = people.find((u) => u && String(u.id) === id);
      const cur = noOne.get(id) || { agentId: id, name: fullName(agent) || "(No Agent on the File)", count: 0 };
      cur.count += r.noOneToGoTo;
      noOne.set(id, cur);
    }
    if (!r.moves.length) return;
    out.deals.push({ dealId: dealIdOf(deal), address: deal.propertyaddress || "(no address)", count: r.moves.length });
    out.total += r.moves.length;
    r.moves.forEach((m) => {
      const id = String(m.to.id);
      const cur = byPerson.get(id) || { id, name: fullName(m.to) || "(no name)", count: 0, from: new Map() };
      cur.count += 1;
      const fromUser = m.from ? people.find((u) => u && String(u.id) === String(m.from.id)) : null;
      const fromName = m.from ? fullName(fromUser) || fullName(m.from) || "(no name)" : m.task.assignee && m.task.assignee.id ? `${fullName(m.task.assignee) || "Someone"} (Left the Team)` : "No Owner";
      cur.from.set(fromName, (cur.from.get(fromName) || 0) + 1);
      byPerson.set(id, cur);
    });
  });
  out.byPerson = [...byPerson.values()]
    .map((p) => ({ ...p, from: [...p.from.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count) }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  out.noOneToGoTo = [...noOne.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return out;
};

// One file's checklist after the fix, decided against the list AS SAVED (mutateDealTasks hands in
// the fresh copy and the deal's own fields). Each moved item is stamped: handedOff* like a Task tab
// hand-off (the Task tab shows "from ..."), cleanedUpBy / cleanedUpAt like Task Cleanup (who and
// when; Undo looks for this stamp), and ownerFixedTo so a later run knows the rule put it there.
// ctx is planOwnerFixes' options for this one file (owners are worked out here from the fresh deal).
// Returns { next, changes: [{ index, before, after }] }.
export const applyOwnerFixes = (saved, deal, ctx = {}, { user, stamp, today } = {}) => {
  const list = Array.isArray(saved) ? saved : [];
  const day = toYMD(today) || todayYMD();
  if (!inScopeDeal(deal, day)) return { next: list, changes: [] };
  if (ctx.agentId && String((deal && deal.agentData) || "") !== String(ctx.agentId)) return { next: list, changes: [] };
  const owners = dealOwners(deal, ctx.users, ctx.backupId);
  const { moves } = ownerFixesForDeal(list, deal, { ...ctx, owners });
  if (!moves.length) return { next: list, changes: [] };
  const by = namedPerson(user);
  const next = [...list];
  const changes = moves.map(({ index, task, key, to }) => {
    const after = cleanDealTask({
      ...task,
      assignee: to,
      routedToLC: key === "lc" && !task.coordinatorRole,
      handedOffFrom: namedPerson(task.assignee),
      handedOffBy: fullName(user),
      handedOffAt: day,
      ownerFixedTo: String(to.id),
      cleanedUpBy: by,
      cleanedUpAt: stamp,
    });
    next[index] = after;
    return { index, before: task, after };
  });
  return { next, changes };
};

// Undo one file's part of a run: each item goes back exactly as it was, but only while it still
// carries THIS run's stamp, the person the run gave it and the same status (an item ticked or handed
// on since keeps that). Returns { next, restored, missed }.
export const undoOwnerFixes = (saved, changes, stamp) => {
  const list = Array.isArray(saved) ? saved : [];
  const next = [...list];
  const used = new Set();
  let restored = 0;
  let missed = 0;
  (Array.isArray(changes) ? changes : []).forEach(({ index, before, after }) => {
    const still = (i) => {
      const t = list[i];
      return (
        !!t && !used.has(i) && String(t.cleanedUpAt || "") === String(stamp) &&
        norm(t.id) === norm(after.id) && norm(t.stage) === norm(after.stage) && norm(t.title) === norm(after.title) &&
        t.status === after.status && String((t.assignee && t.assignee.id) || "") === String((after.assignee && after.assignee.id) || "")
      );
    };
    const i = still(index) ? index : list.findIndex((_, n) => still(n));
    if (i === -1) {
      missed += 1;
      return;
    }
    used.add(i);
    next[i] = before;
    restored += 1;
  });
  return { next: restored ? next : list, restored, missed };
};
